/**
 * 🤖 AUTO-GENERATED API SCHEMA FILE - DO NOT EDIT DIRECTLY!
 *
 * Generated from: broker_submissions entity configuration
 * Generator: scripts/generate-api-clients.ts
 *
 * This file contains the API client for the broker_submissions table with:
 * - Type-safe CRUD operations
 * - Field-level security enforcement
 * - Search functionality with multi-term search and global config integration
 * - Validation using generated schemas
 *
 * @description
 * This client provides a complete interface for interacting with the broker_submissions API.
 * All operations respect the field permissions defined in the entity configuration.
 *
 * Public fields: [id, submitted_by_user_profile_id, submission_status_id, note, submitted_at, reviewed_by_user_profile_id, reviewed_at, review_notes, created_at, updated_at]
 * Searchable fields: []
 * Filterable fields: [id, submitted_by_user_profile_id, submission_status_id, reviewed_by_user_profile_id]
 */

import { httpClient } from '../http-client.js';
import { type UnifiedQueryOptions } from '@config/search/global-search-config';
import type {
  BrokerSubmissionsRow,
  BrokerSubmissionsInsert,
  BrokerSubmissionsUpdate
} from '../../types/generated_schemas/generated-broker_submissions-schema.js';

export interface BrokerSubmissionsQueryOptions extends UnifiedQueryOptions {}

export interface BrokerSubmissionsResponse {
  data: BrokerSubmissionsRow[];
  count?: number;
  success: boolean;
}

/**
 * API Client for broker_submissions table operations
 *
 * @description Provides type-safe access to broker_submissions API endpoints with
 * field-level security enforcement and comprehensive error handling.
 */
export class BrokerSubmissionsClient {
  private readonly basePath = '/broker-submissions';

  /**
   * Get all broker_submissions records with optional filtering and pagination
   *
   * @param options - Query options for filtering, sorting, and pagination
   * @returns Promise resolving to array of broker_submissions records
   */
  async getAll(options?: BrokerSubmissionsQueryOptions): Promise<BrokerSubmissionsRow[]> {
    const params = new URLSearchParams();
    
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.sortBy) params.set('sort', options.sortBy);
    if (options?.sortOrder) params.set('order', options.sortOrder);
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;
    
    const response = await httpClient.get<BrokerSubmissionsResponse>(url);
    return response.data;
  }

  /**
   * Get a single broker_submissions record by ID
   *
   * @param id - Unique identifier for the record
   * @returns Promise resolving to the broker_submissions record
   */
  async getById(id: string): Promise<BrokerSubmissionsRow> {
    const response = await httpClient.get<{
      data: BrokerSubmissionsRow;
      success: boolean;
    }>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new broker_submissions record
   *
   * @param data - Data for creating the new record
   * @returns Promise resolving to the created broker_submissions record
   */
  async create(data: BrokerSubmissionsInsert): Promise<BrokerSubmissionsRow> {
    const response = await httpClient.post<{
      data: BrokerSubmissionsRow;
      success: boolean;
    }>(this.basePath, data);
    return response.data;
  }

  /**
   * Update an existing broker_submissions record
   *
   * @param id - Unique identifier for the record to update
   * @param data - Updated data for the record
   * @returns Promise resolving to the updated broker_submissions record
   */
  async update(id: string, data: BrokerSubmissionsUpdate): Promise<BrokerSubmissionsRow> {
    const response = await httpClient.put<{
      data: BrokerSubmissionsRow;
      success: boolean;
    }>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a broker_submissions record
   *
   * @param id - Unique identifier for the record to delete
   * @returns Promise resolving when deletion is complete
   */
  async delete(id: string): Promise<void> {
    await httpClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Create multiple broker_submissions records in a single request
   *
   * @param data - Array of data for creating new records
   * @returns Promise resolving to array of created broker_submissions records
   */
  async createMany(data: BrokerSubmissionsInsert[]): Promise<BrokerSubmissionsRow[]> {
    const response = await httpClient.post<{
      data: BrokerSubmissionsRow[];
      success: boolean;
    }>(`${this.basePath}/batch`, { items: data });
    return response.data;
  }

  /**
   * Search broker_submissions with text query
   * ⚠️  SEARCH DISABLED: No fields marked as searchable in entity config
   */
  async search(
    _query: string,
    _columns?: string[],
    _options?: BrokerSubmissionsQueryOptions
  ): Promise<BrokerSubmissionsResponse> {
    throw new Error('Search operations are not available for broker_submissions - no searchable fields configured');
  }

  /**
   * Get count of broker_submissions records matching optional filters
   *
   * @param filters - Optional filters to apply when counting
   * @returns Promise resolving to the count of matching records
   */
  async count(filters?: Record<string, any>): Promise<number> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}/count?${queryString}` : `${this.basePath}/count`;
    
    const response = await httpClient.get<{ data: number; success: boolean }>(url);
    return response.data;
  }

  /**
   * Check if a broker_submissions record exists by ID
   *
   * @param id - Unique identifier to check
   * @returns Promise resolving to boolean indicating existence
   */
  async exists(id: string): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch (error) {
      // Assuming 404 means record doesn't exist
      return false;
    }
  }

  /**
   * Get field configuration metadata for this entity
   *
   * @returns Object containing field permission and configuration data
   */
  getFieldConfig() {
    return {
      tableName: 'broker_submissions',
      publicFields: ['id', 'submitted_by_user_profile_id', 'submission_status_id', 'note', 'submitted_at', 'reviewed_by_user_profile_id', 'reviewed_at', 'review_notes', 'created_at', 'updated_at'],
      searchableFields: [],
      filterableFields: ['id', 'submitted_by_user_profile_id', 'submission_status_id', 'reviewed_by_user_profile_id'],
      sortableFields: ['created_at', 'updated_at'],
      readonlyFields: ['id', 'created_at', 'updated_at'],
      requiredFields: ['id', 'submitted_at', 'created_at', 'updated_at'],
    };
  }
}

// Export singleton instance for convenience
export const brokerSubmissionsClient = new BrokerSubmissionsClient();
